import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export interface UserSettings {
  accountId: string | null;
  createdAt?: string | number | null;
  email?: string;
  communicationEmail?: string;
  emailStatus?: 'active' | 'inactive';
  signupFlow?: string | null;
  settings: {
    security: {
      twoFactorAuth: boolean;
      ipWhitelist: string;
      webhookSecret: string;
    };
    notifications: {
      accountEmails: boolean;
      releaseNotesEmails: boolean;
      announcementsEmails: boolean;
    };
    profile: {
      name: string;
      pictureUrl?: string;
      company: string;
      companyWebsite: string;
      companyAddressLine1: string;
      companyAddressLine2: string;
      companyCity: string;
      companyState: string;
      companyPostalCode: string;
      companyCountry: string;
      timezone: string;
      defaultSendingDomain: string;
    };
    workspaceName: string;
  };
  subscription: {
    id: string;
    status: string;
    planName: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    addOns: Record<string, number>;
    adminSubscriptionOverride?: {
      status: 'paid' | 'free' | 'none';
      mode?: 'fallback' | 'override';
      appliedAt?: string;
      appliedBy?: string;
    } | null;
  };
  subscriptionAccess?: {
    active: boolean;
    paid: boolean;
  };
  suspended?: boolean;
  suspensionReason?: string;
  status?: string;
  deletionScheduledFor?: string;
  totalEmailsSent?: number;
  hasSentEmail?: boolean;
  workspaceName?: string;
  configurations?: {
    optionalCompanyAddress?: boolean;
    optionalUnsubscribeUrl?: boolean;
    [key: string]: unknown;
  };
  workspace?: {
    workspaceId: string;
    name: string;
    payingUserId?: string;
    createdAt?: string;
    updatedAt?: string;
    isBillingOwner?: boolean;
    companyProfileWorkspaceId?: string | null;
    isCompanyProfileOwner?: boolean;
    subscriptionAccess?: {
      active: boolean;
      paid: boolean;
    };
    hasSentEmail?: boolean;
    [key: string]: unknown;
  };
  role?: string;
  permissions?: string[];
  workspaces?: { workspaceId: string; name: string; role: string; roleName?: string; isOwner: boolean }[];
}

/**
 * Custom hook for fetching user settings with caching
 * Cached for 5 minutes since settings don't change frequently
 * This includes workspace-scoped technical setup data when permitted, user settings,
 * subscription info, and suspension status.
 */
export function useUserSettings() {
  const { user } = useAuth();

  const query = useQuery<UserSettings>({
    queryKey: ['userSettings', user?.workspaceId],
    queryFn: async () => {
      const response = await apiService.getUserSettings(user?.workspaceId);
      const settings = {
        security: response.security || {
          twoFactorAuth: false,
          ipWhitelist: '',
          webhookSecret: ''
        },
        notifications: response.notifications || {
          accountEmails: true,
          releaseNotesEmails: true,
          announcementsEmails: true,
        },
        profile: {
          name: response.profile?.name || '',
          pictureUrl: response.profile?.pictureUrl || '',
          company: response.profile?.company || '',
          companyWebsite: response.profile?.companyWebsite || '',
          companyAddressLine1: response.profile?.companyAddressLine1 || '',
          companyAddressLine2: response.profile?.companyAddressLine2 || '',
          companyCity: response.profile?.companyCity || '',
          companyState: response.profile?.companyState || '',
          companyPostalCode: response.profile?.companyPostalCode || '',
          companyCountry: response.profile?.companyCountry || '',
          timezone: response.profile?.timezone || '',
          defaultSendingDomain: response.profile?.defaultSendingDomain || '',
        },
        workspaceName: response.workspace?.name || '',
      };

      return {
        accountId: response.accountId,
        createdAt: response.createdAt || null,
        email: response.email,
        communicationEmail: response.communicationEmail || '',
        emailStatus: response.emailStatus || 'active',
        signupFlow: response.signupFlow || null,
        settings,
        subscription: response.workspace?.subscription || {
          id: '',
          status: '',
          planName: '',
          currentPeriodStart: '',
          currentPeriodEnd: '',
          addOns: {},
          adminSubscriptionOverride: null,
        },
        subscriptionAccess: response.workspace?.subscriptionAccess,
        suspended: response.workspace?.suspended || false,
        suspensionReason: response.workspace?.suspensionReason,
        status: response.status,
        deletionScheduledFor: response.deletionScheduledFor,
        totalEmailsSent: response.workspace?.totalEmailsSent || 0,
        hasSentEmail: response.workspace?.hasSentEmail || false,
        workspaceName: response.workspace?.name,
        configurations: response.configurations,
        workspace: response.workspace,
        role: response.role,
        permissions: response.permissions,
        workspaces: response.workspaces,
      };
    },
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // 10 minutes
    // Do not make an account request while authentication is unresolved or
    // after sign-out. Protected pages can briefly mount during redirects.
    enabled: !!user,
    retry: 1, // Only retry once for settings
    // Poll every 45 seconds if subscription status is any pending state
    // Possible pending states: pending-subscription, pending-cancel, pending-change-plan
    refetchInterval: (query) => {
      const status = query.state.data?.subscription?.status?.toLowerCase();
      return status?.startsWith('pending-') ? 45000 : false;
    },
  });

  return query;
}
